import { emptyResources, emptyTotal } from './useQueryResourceList.js';
import { isWoodyError } from '../../errors/errors.utils.js';
import { IntegrationType } from '@fabric/woody-client';
import { useInfiniteQuery, keepPreviousData, hashKey, useQuery } from '@tanstack/react-query';
import { resourceQueryKeys } from './resourceQueryKeys.js';
import { useWoody } from '../../woody/woody.js';
import { isQueryEnabled } from '@fabric-app/queries/utilities';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { isTruthy } from '@fabric-app/utils/guards';
import { mapExternalToResourceDetail } from '../../connections/utils/mapExternalToResourceDetail.js';
import { useMemo } from 'react';
import { useQueryIntegrations } from '../../connections/queries/useQueryIntegrations.js';

const filterSelector = (data) => {
  const allHits = data.pages.flatMap((page) => page.hits);
  const hitsAsResources = allHits.map(mapExternalToResourceDetail).filter(isTruthy);
  return {
    /**
    * The elastic search total could be innacurate, so if we reached the end of the results we rely on the total resources gotten
    * from the query. Otherwise we use the total from the first page.
    */
    // total: hasReachedEnd ? allHits.length : data.pages[0].total,
    total: allHits.length,
    queryHash: data.pages[0]._meta.queryHash,
    pages: data.pages.map((page) => ({
      pageTotal: page.hits.length
    })),
    resources: hitsAsResources
  };
};
const useQueryResourceContextSearchExternal = (params, queryOptions) => {
  const { client } = useWoody();
  const accessToken = useContextAccessToken();
  const pageSize = params?.pageSize ?? 20;
  const query = useInfiniteQuery({
    queryKey: resourceQueryKeys.resourceContextSearchExternalList(params),
    queryFn: async ({ pageParam, signal, queryKey }) => {
      const pagination = {
        page: pageParam ?? 1,
        pageSize
      };
      const pageData = await client.v2("/v2/search-external", {
        method: "post",
        body: {
          query: params.query
        },
        query: {
          accessToken
        },
        signal
      });
      return {
        // This allows the select function to have access to the options used in the query
        // And we avoid having to memoize the select function
        nextCursor: null,
        // total: pageData.total,
        // hasMore: pageData.hasMore,
        total: pageData.hits.length,
        hasMore: false,
        hits: pageData.hits,
        _meta: {
          queryHash: hashKey(queryKey),
          options: params,
          pagination
        }
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 15e3,
    refetchInterval: 45e3,
    retry: (failureCount, error) => {
      if (isWoodyError(error) && error.status === 404) {
        return failureCount < 1;
      }
      return failureCount < 4;
    },
    ...queryOptions,
    select: filterSelector,
    enabled: isQueryEnabled([
      queryOptions?.enabled,
      !!params
    ]),
    initialPageParam: 1,
    getNextPageParam: (_lastPage) => {
      return void 0;
    }
  });
  const dropboxReferenceIds = useMemo(() => {
    const result = {};
    query.data?.resources.forEach((resource) => {
      if (resource._meta?.externalData?.integrationId && resource._meta?.externalData?.provider === IntegrationType.DROPBOX) {
        result[resource._meta?.externalData?.integrationId] = [
          ...result[resource._meta?.externalData?.integrationId] ?? [],
          resource._meta?.externalData?.reference.id
        ];
      }
    });
    return result;
  }, [
    query.data?.resources
  ]);
  const { data: dropboxThumbnails } = useQuery({
    queryKey: [
      "dropbox-thumbnails",
      dropboxReferenceIds
    ],
    queryFn: async () => {
      if (Object.keys(dropboxReferenceIds).length === 0) {
        return {};
      }
      const thumbnails = await Promise.all(Object.entries(dropboxReferenceIds).map(([integrationId, referenceIds]) => {
        return client.v2({
          endpoint: "/v2/integration/{integrationId}/dropbox/thumbnails",
          params: {
            integrationId
          }
        }, {
          method: "post",
          body: {
            referenceIds
          }
        });
      }));
      return thumbnails.reduce((acc, curr) => {
        return {
          ...acc,
          ...curr.thumbnails
        };
      }, {});
    }
  });
  const resources = useMemo(() => {
    if (!query.data?.resources || query.data?.resources.length === 0) {
      return emptyResources;
    }
    if (!dropboxThumbnails || Object.keys(dropboxThumbnails).length === 0) {
      return query.data.resources;
    }
    return query.data?.resources.map((resource) => {
      const fetchedThumbnail = dropboxThumbnails[resource.id];
      if (!fetchedThumbnail) {
        return resource;
      }
      return {
        ...resource,
        thumbnail: {
          lg: fetchedThumbnail,
          md: fetchedThumbnail,
          sm: fetchedThumbnail,
          xl: fetchedThumbnail,
          original: fetchedThumbnail
        }
      };
    }) || emptyResources;
  }, [
    query.data?.resources,
    dropboxThumbnails
  ]);
  return {
    ...query,
    resources,
    total: query.data?.total ?? emptyTotal,
    queryHash: query.data?.queryHash
  };
};
const useQueryResourceContextSearchExternalWarmer = () => {
  const { integrations } = useQueryIntegrations();
  const shouldFetch = useMemo(() => integrations.some((integration) => [
    IntegrationType.DROPBOX,
    IntegrationType.GOOGLE_DRIVE
  ].includes(integration.type)), [
    integrations
  ]);
  useQueryResourceContextSearchExternal({
    query: "token warming not hack at all"
  }, {
    enabled: shouldFetch,
    refetchInterval: 1e3 * 60 * 30
  });
};

export { useQueryResourceContextSearchExternal, useQueryResourceContextSearchExternalWarmer };
//# sourceMappingURL=useQueryResourceContextSearchExternal.js.map
